import React, { useState, useRef, useEffect } from 'react';
import { TextField, Paper, Typography, Box, CircularProgress } from '@mui/material';
import { LocationOn as LocationIcon } from '@mui/icons-material';
import api from '../../utils/api';

/**
 * Google Places autocomplete input that returns address + lat/lng on selection.
 */
const PlaceAutocompleteInput = ({
  value = '',
  onChange,
  onSelect,
  label = 'Search for a place',
  placeholder = 'Enter city, town or address...',
  disabled = false,
  error = false,
  helperText = ''
}) => {
  const [inputValue, setInputValue] = useState(value);
  useEffect(() => {
    setInputValue(value);
  }, [value]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  const fetchSuggestions = async (query) => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setLoading(true);
    try {
      const response = await api.get('/api/geocode/places', {
        params: { input: query.trim() }
      });
      const predictions = response.data?.data?.predictions || [];
      setSuggestions(predictions);
      setShowSuggestions(predictions.length > 0);
    } catch {
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value || '';
    setInputValue(newValue);
    if (onChange) onChange(newValue);
    if (!newValue.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(newValue.trim()), 300);
  };

  const handleSuggestionSelect = async (suggestion) => {
    const address = suggestion?.description || '';
    if (!address) return;
    setInputValue(address);
    setShowSuggestions(false);
    setSuggestions([]);
    if (onChange) onChange(address);

    setLoading(true);
    try {
      const response = await api.post('/api/geocode/address', { address });
      if (response.data?.success && response.data?.data?.coordinates) {
        const [lng, lat] = response.data.data.coordinates;
        if (onSelect) {
          onSelect({
            address: response.data.data.formattedAddress || address,
            latitude: lat,
            longitude: lng
          });
        }
      }
    } catch {
      if (onSelect) onSelect({ address, latitude: null, longitude: null });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        inputRef.current?.contains(e.target) ||
        suggestionsRef.current?.contains(e.target)
      ) return;
      setShowSuggestions(false);
    };
    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSuggestions]);

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <TextField
        ref={inputRef}
        fullWidth
        size="small"
        label={label}
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        disabled={disabled}
        error={error}
        helperText={helperText}
        InputProps={{
          startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
          endAdornment: loading && <CircularProgress size={20} />
        }}
      />
      {showSuggestions && suggestions.length > 0 && !loading && (
        <Paper
          ref={suggestionsRef}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1300,
            maxHeight: 200,
            overflow: 'auto',
            boxShadow: 3,
            mt: 0.5,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          {suggestions.map((s, i) => (
            <Box
              key={s.place_id || i}
              sx={{
                p: 1.5,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
                borderBottom: i < suggestions.length - 1 ? 1 : 0,
                borderColor: 'divider'
              }}
              onClick={() => handleSuggestionSelect(s)}
            >
              <Typography variant="body2" fontWeight={500}>
                {s.structured_formatting?.main_text || s.description || ''}
              </Typography>
              {s.structured_formatting?.secondary_text && (
                <Typography variant="caption" color="text.secondary">
                  {s.structured_formatting.secondary_text}
                </Typography>
              )}
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
};

export default PlaceAutocompleteInput;
